import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Stack,
  TextField,
  MenuItem,
  Grid
} from "@mui/material";

import PageHeader from "../../components/common/PageHeader";
import { getReceivables } from "../../api/accountingApi";

export default function Receivables() {

  const [receivables, setReceivables] = useState([]);

  const [filters, setFilters] = useState({
    status: "",
    date_from: "",
    date_to: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getReceivables(filters);
      setReceivables(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Receivables Error:", err);
    }
  };

  // =========================
  // HANDLE FILTER CHANGE
  // =========================
  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    loadData();
  };

  // =========================
  // STATUS COLOR
  // =========================
  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIAL":
        return "warning";
      default:
        return "error";
    }
  };

  // =========================
  // TOTALS CALCULATION
  // =========================
  const totals = receivables.reduce(
    (acc, r) => {
      acc.total += r.amount || 0;
      acc.outstanding += r.outstanding || r.amount || 0;
      return acc;
    },
    { total: 0, outstanding: 0 }
  );

  return (
    <Box className="page">

      {/* HEADER */}
      <PageHeader
        title="Receivables"
        subtitle="Manage customer invoices and collections"
      />

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>

          <Grid item xs={3}>
            <TextField
              select
              label="Status"
              name="status"
              fullWidth
              value={filters.status}
              onChange={handleFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={3}>
            <TextField
              type="date"
              label="From Date"
              name="date_from"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              type="date"
              label="To Date"
              name="date_to"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={3}>
            <Button
              variant="contained"
              fullWidth
              sx={{ height: "100%" }}
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* TOTALS BAR */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={5}>
          <Typography variant="h6">
            Total Invoices: ₹ {totals.total}
          </Typography>

          <Typography variant="h6" color="error">
            Outstanding: ₹ {totals.outstanding}
          </Typography>
        </Stack>
      </Paper>

      {/* ACTION BAR */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Invoice List</Typography>

        <Button variant="contained">
          Create Invoice
        </Button>
      </Stack>

      {/* TABLE */}
      <Paper sx={{ borderRadius: 3 }}>

        <Table>

          <TableHead>
            <TableRow>
              <TableCell><b>Invoice #</b></TableCell>
              <TableCell><b>Amount</b></TableCell>
              <TableCell><b>Paid</b></TableCell>
              <TableCell><b>Outstanding</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Action</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            {receivables.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Receivables Found
                </TableCell>
              </TableRow>
            ) : (
              receivables.map((r) => (
                <TableRow key={r.id} hover>

                  <TableCell>{r.invoice_number}</TableCell>

                  <TableCell>₹ {r.amount}</TableCell>

                  <TableCell>₹ {r.paid_amount || 0}</TableCell>

                  <TableCell>
                    ₹ {r.outstanding || r.amount}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={r.status}
                      color={getStatusColor(r.status)}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    {r.invoice_date || "-"}
                  </TableCell>

                  {/* 🔥 RECEIVE BUTTON */}
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={r.status === "PAID"}
                    >
                      Receive
                    </Button>
                  </TableCell>

                </TableRow>
              ))
            )}

          </TableBody>

        </Table>

      </Paper>
    </Box>
  );
}